/**
 * @file 面试 - 基本信息
 */
import { Button, ButtonAdd } from '@seiue/ui'
import { Form, Input } from 'antd'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { GrowpIcon } from 'packages/components/GrowpIcon'
import { InterviewActivityTaskResultSettingsReq } from 'packages/sdks-next/growp'

import { CommonBasicInfo } from '@/components/CommonBasicInfo'

import { BasicInfoProps } from '../types'
import { EntryComponentsWrapper } from 'packages/theme'

/**
 * 面试 - 基本信息
 *
 * @param param0 - 参数
 * @param param0.form - 表单实例
 * @param param0.basicInfoRef - 基本信息 ref
 * @param param0.commonBasicInfo - 基本信息
 * @param param0.setTitle - 设置标题
 * @param param0.activityTaskOptions - 任务选项
 * @param param0.openTypeDisabled - 是否关闭开启设置
 * @param param0.initResults - 初始结果
 * @returns  component
 */
export const BasicInfo: React.FC<
  BasicInfoProps & {
    openTypeDisabled?: boolean
    initResults?: InterviewActivityTaskResultSettingsReq[]
  }
> = ({
  form,
  basicInfoRef,
  commonBasicInfo,
  setTitle,
  activityTaskOptions,
  openTypeDisabled,
  initResults,
}) => {
  const [resultList, setResultList] = useState<
    InterviewActivityTaskResultSettingsReq[]
  >(
    initResults ?? [
      { interviewResult: '已通过' },
      { interviewResult: '未通过' },
      { interviewResult: '待定' },
    ]
  )

  useEffect(() => {
    if (form) {
      form.setFieldValue('interviewResults', resultList)
    }
  }, [resultList, form])

  return (
    <Wrapper>
      <Form
        colon={false}
        form={form}
        wrapperCol={{ span: 22 }}
        labelCol={{ span: 2 }}
        initialValues={{
          isShow: true,
          interviewResults: resultList,
        }}
      >
        <Form.Item
          label="面试标题"
          name="name"
          rules={[{ required: true, message: '请输入面试标题' }]}
        >
          <Input
            placeholder="请输入面试标题"
            onChange={(e) => {
              setTitle && setTitle(e.target.value)
            }}
            maxLength={200}
          />
        </Form.Item>
        <Form.Item
          label="面试结果"
          name="interviewResults"
          required={true}
          rules={[
            {
              validator: (rule, value, callback) => {
                const currData = form?.getFieldValue([
                  'interviewResults',
                ]) as InterviewActivityTaskResultSettingsReq[]
                currData.map((e) => {
                  if (e.interviewResult === '') {
                    callback('请填写面试结果')
                  }
                })
                callback()
              },
            },
          ]}
        >
          <ResultBox>
            <ExtraBox>{'可自定义面试结果的状态'}</ExtraBox>
            {resultList.map((e, key) => {
              return (
                <Form.Item
                  noStyle
                  key={`${e.interviewResult}-${key}`}
                  required={false}
                  name={['interviewResults', `${key}`]}
                  rules={[
                    {
                      validator: (rule, value, callback) => {
                        if (value === '' || value === undefined) {
                          callback('请填写面试结果')
                        }
                        callback()
                      },
                    },
                  ]}
                >
                  <InputBox>
                    <Input
                      style={{ width: '400px', marginRight: '16px' }}
                      maxLength={20}
                      defaultValue={e.interviewResult}
                      placeholder="请输入"
                      onBlur={(input) => {
                        resultList[key] = {
                          interviewResult: input.target.value,
                          id: e.id,
                        }
                        setResultList([...resultList])
                        form?.validateFields(['interviewResults', `${key}`])
                      }}
                    />
                    {resultList.length > 1 ? (
                      <Button
                        onClick={() => {
                          resultList.splice(key, 1)
                          setResultList([...resultList])
                        }}
                        type="link"
                      >
                        <GrowpIcon name="Cuohao" />
                      </Button>
                    ) : null}
                  </InputBox>
                </Form.Item>
              )
            })}
          </ResultBox>

          <ButtonAdd
            disabled={resultList.length >= 20 ? true : false}
            onClick={() =>
              setResultList([...resultList, { interviewResult: '' }])
            }
            style={{
              fontWeight: 400,
            }}
          >
            添加面试结果
          </ButtonAdd>
        </Form.Item>

        <CommonBasicInfo
          form={form}
          ref={basicInfoRef}
          value={commonBasicInfo}
          activityTaskOptions={activityTaskOptions || []}
          openTypeDisabled={!!openTypeDisabled}
        />
      </Form>
    </Wrapper>
  )
}

const Wrapper = styled(EntryComponentsWrapper)`
  &&& {
    .ant-form-item {
      margin-bottom: 20px;
    }
  }
`

const ResultBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 6px;
  margin-bottom: 4px;
`

const ExtraBox = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${(props) => props.theme.text._3};
`

const InputBox = styled.div`
  display: flex;
`
